import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ProjectDialog } from "@/components/admin/ProjectDialog";
import { ConfirmDelete } from "@/components/admin/shared";
import { ProgressBar, StatusBadge, UnreadBadge, fmtDue } from "@/components/project/ProjectBits";
import { AdminHeader, EmptyState } from "./AdminLayout";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

export default function Projects() {
  const [projects, setProjects] = useState(null);
  const [dialog, setDialog] = useState({ open: false, item: null });
  const [toDelete, setToDelete] = useState(null);

  const load = () => api.get("/projects").then((r) => setProjects(r.data)).catch((e) => { setProjects([]); err(e); });
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    if (dialog.item) await api.put(`/admin/projects/${dialog.item.id}`, payload);
    else await api.post("/admin/projects", payload);
    toast.success(dialog.item ? "Project updated" : "Project created");
    setDialog({ open: false, item: null });
    load();
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/projects/${toDelete.id}`);
      toast.success("Project deleted");
      setToDelete(null);
      load();
    } catch (e) { err(e); }
  };

  const add = (
    <button onClick={() => setDialog({ open: true, item: null })} data-testid="admin-add-project-button" className="btn-glow inline-flex items-center gap-2 rounded-full bg-cyan-400 px-5 py-2.5 text-sm font-semibold text-[#07090E] hover:bg-cyan-300">
      <Plus className="h-4 w-4" /> New project
    </button>
  );

  return (
    <div data-testid="admin-projects-page">
      <AdminHeader eyebrow="// delivery" title="Projects" action={add}>Every client project, its status and progress.</AdminHeader>
      {projects === null ? <div className="h-40 animate-pulse rounded-2xl bg-white/[0.03]" /> : projects.length === 0 ? (
        <EmptyState icon={Briefcase} title="No projects yet" text="Create a project and assign it to a client." testId="admin-projects-empty-state" />
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-white/5 bg-[#0B0F18]">
          <table data-testid="admin-projects-table" className="w-full min-w-[860px] text-left text-sm">
            <thead className="border-b border-white/5 font-mono-tech text-[11px] uppercase tracking-wider text-slate-500">
              <tr>{["Project", "Client", "Status", "Progress", "Due", "Unread", ""].map((h) => <th key={h} className="px-5 py-3">{h}</th>)}</tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr key={p.id} data-testid={`project-row-${p.id}`} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                  <td className="px-5 py-4"><Link to={`/admin/projects/${p.id}`} data-testid={`project-open-${p.id}`} className="font-medium text-white hover:text-cyan-300">{p.name}</Link></td>
                  <td className="px-5 py-4 text-slate-400">{p.client_name}</td>
                  <td className="px-5 py-4"><StatusBadge status={p.status} testId={`project-status-${p.id}`} /></td>
                  <td className="w-48 px-5 py-4"><ProgressBar value={p.progress} testId={`project-progress-${p.id}`} /></td>
                  <td className="whitespace-nowrap px-5 py-4 text-slate-400">{fmtDue(p.due_date)}</td>
                  <td className="px-5 py-4"><UnreadBadge count={p.unread_count} testId={`project-unread-${p.id}`} /></td>
                  <td className="whitespace-nowrap px-5 py-4 text-right">
                    <button onClick={() => setDialog({ open: true, item: p })} data-testid={`project-edit-${p.id}`} aria-label="Edit" className="rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-cyan-300"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => setToDelete(p)} data-testid={`project-delete-${p.id}`} aria-label="Delete" className="rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ProjectDialog open={dialog.open} item={dialog.item} onClose={() => setDialog({ open: false, item: null })} onSave={save} />
      <ConfirmDelete target={toDelete} label={toDelete ? `"${toDelete.name}" and all its files and messages` : ""} onCancel={() => setToDelete(null)} onConfirm={remove} />
    </div>
  );
}
